/* eslint-disable react/jsx-no-bind -- one-line forwarding to non-memo'd children; component-anatomy.md lists this as a narrow exception */
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import SvgIcon from '@mui/material/SvgIcon'
import React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import AccountSelectionDropdown from 'containers/select-account/AccountSelectionDropdown'
import { setSelectedLoginDetails } from 'slices/login/LoginPersistSlice'
import { Z_INDEX } from 'theme/zIndex'

const { MENU } = Z_INDEX

export interface UserMenuItem {
  title: string
  icon: React.ReactNode
  onClick?: () => void
  url?: string
}

interface Business {
  businessId: string
  businessName: string
}

interface LoginPersistState {
  LoginPersist?: {
    selectedLoginDetails?: { businessId?: string }
    allBusinesses?: Business[]
  }
}

const NoIcon = () => null
const ITEM_SX = { height: 40, display: 'flex', borderTop: '1px solid', borderColor: 'divider' }

interface UserMenuProps {
  anchorEl: HTMLElement | null
  items: UserMenuItem[]
  onClose: () => void
}

const UserMenu = ({ anchorEl, items, onClose }: UserMenuProps) => {
  const dispatch = useDispatch()
  const selectedBusinessId = useSelector((state: LoginPersistState) => state.LoginPersist?.selectedLoginDetails?.businessId, shallowEqual)
  const allBusinesses = useSelector((state: LoginPersistState) => state.LoginPersist?.allBusinesses ?? [], shallowEqual)
  const isOnlyOneBusiness = allBusinesses.length === 1

  const handleBusinessChange = (event: { target: { value: string } }): void => {
    const currentBusiness = allBusinesses.find(business => business.businessId === event.target.value)
    dispatch(
      setSelectedLoginDetails({
        show: false,
        businessName: currentBusiness?.businessName,
        businessId: currentBusiness?.businessId,
      })
    )
    setTimeout(() => {
      window.location.href = '/'
    }, 100)
  }

  const handleItemClick = (onClick?: () => void): void => {
    onClose()
    onClick?.()
  }

  const renderItemContent = ({ icon, title }: UserMenuItem) => (
    <>
      <SvgIcon fontSize="small" sx={{ width: 57, color: 'action.active' }}>
        {icon}
      </SvgIcon>
      <Box ml={1} width="100%">
        {title}
      </Box>
    </>
  )

  return (
    <Menu
      disableEnforceFocus
      keepMounted
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      id="menu-appbar"
      open={Boolean(anchorEl)}
      slotProps={{ paper: { sx: { '& ul': { p: 0 } } } }}
      sx={{ zIndex: MENU, mt: 4.5 }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={onClose}
    >
      <AccountSelectionDropdown
        readOnly={isOnlyOneBusiness}
        sx={{ pointerEvents: isOnlyOneBusiness ? 'none' : 'inherit', '& fieldset': { border: 'none' } }}
        value={selectedBusinessId}
        onChange={handleBusinessChange}
        {...(isOnlyOneBusiness && { inputProps: { IconComponent: NoIcon } })}
      />
      {items.map(item =>
        item.url ? (
          <MenuItem key={item.title} sx={{ ...ITEM_SX, p: 0 }}>
            <Link
              href={item.url}
              rel="noopener"
              sx={{ width: '100%', textDecoration: 'none', color: 'inherit', display: 'flex', py: 0.75, px: 2 }}
              target="_blank"
              onClick={onClose}
            >
              {renderItemContent(item)}
            </Link>
          </MenuItem>
        ) : (
          <MenuItem key={item.title} sx={ITEM_SX} onClick={() => handleItemClick(item.onClick)}>
            {renderItemContent(item)}
          </MenuItem>
        )
      )}
    </Menu>
  )
}

export default UserMenu
